import { useMemo } from "react";
import { useData } from "@/context/DataContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCheck, Trash2, Bell } from "lucide-react";

function formatWhen(iso) {
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function NotificationsManager({ open, onOpenChange }) {
  const { notifications, markNotifRead, markAllNotifRead, deleteNotif, clearAllNotifs } = useData();
  const unread = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="notifications-manager"
        className="tp-glass-strong border-white/10 text-white max-w-lg p-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
          <DialogTitle className="text-white flex items-center gap-2">
            <Bell size={18} className="text-fuchsia-300" /> Gestione notifiche
            {unread > 0 && (
              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md tp-brand-gradient text-white">
                {unread} non lette
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 px-5 py-2.5 border-b border-white/5">
          <Button
            data-testid="notif-mgr-mark-all"
            variant="outline"
            size="sm"
            disabled={unread === 0}
            onClick={markAllNotifRead}
            className="h-8 rounded-full border-white/10 text-zinc-300 hover:bg-white/5 text-xs disabled:opacity-40"
          >
            <CheckCheck size={13} className="mr-1.5" /> Segna tutte lette
          </Button>
          <Button
            data-testid="notif-mgr-clear-all"
            variant="outline"
            size="sm"
            disabled={notifications.length === 0}
            onClick={clearAllNotifs}
            className="h-8 rounded-full border-red-400/30 text-red-300 hover:bg-red-500/10 text-xs disabled:opacity-40"
          >
            <Trash2 size={13} className="mr-1.5" /> Cancella tutte
          </Button>
        </div>

        <ScrollArea className="max-h-[55vh]">
          {notifications.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-zinc-500 italic">
              Nessuna notifica.
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                data-testid={`notif-mgr-item-${n.id}`}
                className={`flex items-start gap-3 px-5 py-3 border-b border-white/5 last:border-0 ${
                  !n.read ? "bg-white/[0.02]" : ""
                }`}
              >
                <button
                  onClick={() => !n.read && markNotifRead(n.id)}
                  className="flex items-start gap-2 flex-1 min-w-0 text-left"
                  title={n.read ? "Letta" : "Segna come letta"}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                      !n.read ? "tp-brand-gradient" : "bg-zinc-600"
                    }`}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-white leading-snug">{n.message}</span>
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      {formatWhen(n.created_at)}
                    </span>
                  </span>
                </button>
                <button
                  data-testid={`notif-mgr-delete-${n.id}`}
                  onClick={() => deleteNotif(n.id)}
                  className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                  title="Elimina notifica"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
